const NEUTRAL_THRESHOLD = 0.03

export function isAtCenter(x, y) {
  return Math.abs(x - 0.5) < NEUTRAL_THRESHOLD && Math.abs(y - 0.5) < NEUTRAL_THRESHOLD
}

export function getQuadrantColor(x, y) {
  if (isAtCenter(x, y)) return '#8A8780'
  if (x < 0.5 && y < 0.5) return '#BA7517'   // Excited — amber
  if (x >= 0.5 && y < 0.5) return '#E24B4A'  // Stressed — red
  if (x < 0.5 && y >= 0.5) return '#1D9E75'  // Calm — teal
  return '#5F5E5A'                             // Drained — gray
}

export function describeMood(x, y) {
  if (isAtCenter(x, y)) {
    return 'Balanced, in the middle'
  }

  const isHighEnergy = y < 0.5
  const isPleasant = x < 0.5

  let quadrant
  if (isHighEnergy && isPleasant) quadrant = 'excited'
  else if (isHighEnergy && !isPleasant) quadrant = 'stressed'
  else if (!isHighEnergy && isPleasant) quadrant = 'calm'
  else quadrant = 'drained'

  const dx = Math.abs(x - 0.5)
  const dy = Math.abs(y - 0.5)
  const dist = Math.sqrt(dx * dx + dy * dy)
  const normalizedDist = Math.min(1, dist / Math.sqrt(0.5))

  let intensity
  if (normalizedDist < 0.3) intensity = 'mildly'
  else if (normalizedDist < 0.7) intensity = 'mostly'
  else intensity = 'strongly'

  return `${intensity} ${quadrant}`
}

// --- Legacy dial-based helpers (used by MoodCompassDial.jsx) ---

const CARDINALS = [
  { name: 'excited',  center: 0,   label: 'Excited',  quadrant: 'high energy, pleasant' },
  { name: 'stressed', center: 90,  label: 'Stressed', quadrant: 'high energy, unpleasant' },
  { name: 'drained',  center: 180, label: 'Drained',  quadrant: 'low energy, unpleasant' },
  { name: 'calm',     center: 270, label: 'Calm',     quadrant: 'low energy, pleasant' },
]

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function angularDistance(a, b) {
  const d = Math.abs(a - b)
  return Math.min(d, 360 - d)
}

export function interpretMood(angle, intensity) {
  if (intensity < 0.15) {
    return { label: 'Balanced', subtitle: 'Holding steady in the middle' }
  }

  let primary = CARDINALS[0]
  let primaryDist = 360
  for (const c of CARDINALS) {
    const d = angularDistance(angle, c.center)
    if (d < primaryDist) {
      primary = c
      primaryDist = d
    }
  }

  let intensityDesc
  if (intensity < 0.4) intensityDesc = 'Mildly'
  else if (intensity < 0.75) intensityDesc = 'Mostly'
  else intensityDesc = 'Strongly'

  if (primaryDist < 15) {
    return {
      label: `${intensityDesc} ${primary.label.toLowerCase()}`,
      subtitle: capitalize(primary.quadrant),
    }
  }

  let secondary = null
  let secondaryDist = 360
  for (const c of CARDINALS) {
    if (c.name === primary.name) continue
    const d = angularDistance(angle, c.center)
    if (d < secondaryDist) {
      secondary = c
      secondaryDist = d
    }
  }

  return {
    label: `${primary.label}, leaning ${secondary.label.toLowerCase()}`,
    subtitle: `${capitalize(primary.quadrant)}, with a hint of ${secondary.label.toLowerCase()}`,
  }
}

export function getNeedleColorForAngle(angle) {
  if (angle >= 315 || angle < 45) return '#854F0B'
  if (angle >= 45 && angle < 135) return '#791F1F'
  if (angle >= 135 && angle < 225) return '#2C2C2A'
  return '#0F6E56'
}

export function getVerdictPalette(angle, intensity) {
  if (intensity < 0.15) {
    return {
      bgTint: '#F1EFE8',
      accentColor: '#B4B2A9',
      titleColor: '#444441',
      subtitleColor: '#5F5E5A',
    }
  }
  if (angle >= 315 || angle < 45) {
    return {
      bgTint: '#FAEEDA',
      accentColor: '#BA7517',
      titleColor: '#412402',
      subtitleColor: '#633806',
    }
  }
  if (angle >= 45 && angle < 135) {
    return {
      bgTint: '#FCEBEB',
      accentColor: '#E24B4A',
      titleColor: '#791F1F',
      subtitleColor: '#A32D2D',
    }
  }
  if (angle >= 135 && angle < 225) {
    return {
      bgTint: '#F1EFE8',
      accentColor: '#5F5E5A',
      titleColor: '#2C2C2A',
      subtitleColor: '#444441',
    }
  }
  return {
    bgTint: '#E1F5EE',
    accentColor: '#1D9E75',
    titleColor: '#04342C',
    subtitleColor: '#0F6E56',
  }
}
